import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MdAlbum, MdCheckCircle, MdHeadset, MdHourglassEmpty, MdInfoOutline, MdLock, MdPeople, MdPayments, MdAccountBalanceWallet, MdCalendarToday } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';

// The artist page's "Visão geral", "Vendas" and "Dados bancários" tabs — the
// same as the web studio, seen by the artist's distributor. "Audiência" lives in
// ArtistAudienceTab.js.

const fmtMoney = (amount, currency) => {
    if (currency === 'AOA') return `${new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(amount)} Kz`;
    if (currency === 'EUR' || currency === 'USD') return new Intl.NumberFormat('pt-PT', { style: 'currency', currency }).format(amount);
    return `${new Intl.NumberFormat('pt-PT').format(amount)} ${currency}`;
};
const fmtCount = (n) => new Intl.NumberFormat('pt-PT').format(n || 0);
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const fmtMonth = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[(m || 1) - 1]} ${y}`; };
const fmtDate = (s) => {
    if (!s) return '—';
    const d = new Date(String(s).replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
};
const moneyList = (lines) => (lines && lines.length ? lines.map((l) => fmtMoney(l.gross, l.currency)).join(' + ') : '—');
const METHOD = {
    multicaixa: 'Multicaixa Express', multicaixa_express: 'Multicaixa Express', appy_pay: 'Multicaixa Express', appypay: 'Multicaixa Express',
    stripe: 'Cartão', card: 'Cartão', apple_pay: 'Apple', apple_iap: 'Apple', revenuecat: 'Apple', transference: 'Transferência', points: 'Pontos',
};
const TYPE_LABEL = { single: 'Single', ep: 'EP', album: 'Álbum' };

const card = 'rounded-xl border border-gray-200 bg-white';
const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';

function Stat({ icon, label, value, hint }) {
    return (
        <div className={`${card} min-w-0 p-4`}>
            <div className="mb-1.5 flex items-center gap-2 text-gray-500">
                <span className="shrink-0 text-amber-600">{icon}</span>
                <span className="truncate text-xs font-semibold uppercase tracking-wide">{label}</span>
            </div>
            <div className="break-words text-xl font-bold text-gray-900">{value}</div>
            {hint && <div className="mt-0.5 text-xs text-gray-500">{hint}</div>}
        </div>
    );
}

/** "2 out – 31 out" (or "nov 2025 – out 2026" with months): the dates a number covers. */
const fmtRange = (from, to, months = false) => {
    if (!from || !to) return '';
    const [fy, fm, fd] = from.split('-').map(Number);
    const [ty, tm, td] = to.split('-').map(Number);
    const a = months ? MONTHS[fm - 1] : `${fd} ${MONTHS[fm - 1]}`;
    const b = months ? MONTHS[tm - 1] : `${td} ${MONTHS[tm - 1]}`;
    if (fy !== ty || months) return `${a} ${fy} – ${b} ${ty}`;
    if (ty !== new Date().getFullYear()) return `${a} – ${b} ${ty}`;
    return `${a} – ${b}`;
};

/** "+12% vs 2 set – 1 out": the change against the period before, named by its dates. */
function Delta({ now, before, vs }) {
    if (!before) return now > 0 ? <span className="text-green-700">Novo · 0 em {vs}</span> : null;
    const pct = Math.round(((now - before) / before) * 100);
    if (pct === 0) return <span>Igual a {vs}</span>;
    return <span className={pct > 0 ? 'text-green-700' : 'text-red-600'}>{pct > 0 ? '+' : ''}{pct}% <span className="text-gray-500">vs {vs}</span></span>;
}

/** Loads the figures once per artist; shared by the two first tabs. */
export function useArtistFinance(artistId) {
    // disabled: not released to this artist by the admin (Definições).
    const [state, setState] = useState({ data: null, failed: false, disabled: false });
    useEffect(() => {
        let alive = true;
        setState({ data: null, failed: false, disabled: false });
        izenuAPI.get('/distributor/artist/finance.php', { params: { artist_id: artistId } })
            .then(({ data }) => { if (alive) setState({ data, failed: false, disabled: false }); })
            .catch((err) => {
                if (!alive) return;
                const disabled = err?.response?.data?.code === 'insights_disabled';
                setState({ data: null, failed: !disabled, disabled });
            });
        return () => { alive = false; };
    }, [artistId]);
    return state;
}

// The two kinds of numbers, never mixed: streams (listening, not money) and
// album sales (money from store purchases). Same titles as the web studio.
const KIND = {
    streams: { title: 'Reproduções (streams)', line: 'Quantas vezes ouviram as músicas na Izenu. Não é dinheiro.', tone: 'border-violet-200 bg-violet-50/50', chip: 'bg-violet-100 text-violet-700' },
    sales: { title: 'Vendas de álbuns', line: 'Dinheiro de quem comprou os lançamentos na loja.', tone: 'border-emerald-200 bg-emerald-50/50', chip: 'bg-emerald-100 text-emerald-700' },
};

function KindBox({ kind, icon, action, children }) {
    const k = KIND[kind];
    return (
        <section className={`rounded-xl !border !border-solid p-4 sm:p-5 ${k.tone}`}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${k.chip}`}>{icon}</span>
                    <div className="min-w-0">
                        <h3 className="font-bold leading-tight text-gray-900">{k.title}</h3>
                        <p className="text-xs text-gray-500">{k.line}</p>
                    </div>
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

const GLOSSARY = [
    ['streams', [
        ['Reproduções · 30 dias', 'Vezes que as músicas foram ouvidas nos últimos 30 dias, a partir de 30 segundos de música, comparadas com os 30 dias anteriores.'],
        ['Ouvintes · 30 dias', 'Pessoas diferentes que ouviram. Quem ouve dez vezes conta como um ouvinte.'],
        ['Audiência', 'Origem das reproduções, playlists, países, cidades, idade e género, e números por música estão na aba Audiência.'],
    ]],
    ['sales', [
        ['Total vendido', 'Soma das compras pagas dos lançamentos na loja, desde sempre, pelo valor bruto — antes da parte da Izenu.'],
        ['Este mês', 'Compras confirmadas desde o dia 1 do mês.'],
        ['Por confirmar', 'Compras com o pagamento ainda pendente (por exemplo, um Multicaixa Express por aprovar). Ainda não contam no total.'],
        ['Por lançamento · Últimas compras', 'O detalhe de cada compra. Quem comprou não é mostrado.'],
    ]],
];

/** "Como ler estes números": a quiet key, closed by default, at the bottom. */
function HowToRead() {
    return (
        <details className="group rounded-xl !border !border-solid border-gray-200 bg-white text-sm">
            <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-gray-500 hover:text-gray-800 [&::-webkit-details-marker]:hidden">
                <MdInfoOutline size={16} />
                <span>Como ler estes números</span>
                <span className="ml-auto text-xs transition-transform group-open:rotate-180">▾</span>
            </summary>
            <div className="grid grid-cols-1 gap-4 px-4 pb-4 lg:grid-cols-2">
                {GLOSSARY.map(([kind, rows]) => (
                    <div key={kind}>
                        <p className="font-semibold text-gray-900">{KIND[kind].title}</p>
                        <p className="mb-2 text-xs text-gray-500">{KIND[kind].line}</p>
                        <dl className="space-y-2">
                            {rows.map(([term, desc]) => (
                                <div key={term}>
                                    <dt className="text-xs font-medium text-gray-800">{term}</dt>
                                    <dd className="text-xs leading-relaxed text-gray-500">{desc}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                ))}
            </div>
            <p className="px-4 pb-4 text-xs text-gray-400">Os royalties de streaming — o dinheiro que as reproduções geram — são calculados pela Izenu no fecho de cada mês e ainda não aparecem aqui.</p>
        </details>
    );
}

function Loading({ failed }) {
    return <p className="py-12 text-center text-sm text-gray-500">{failed ? 'Não foi possível carregar. Tenta mais tarde.' : 'A carregar…'}</p>;
}

export function OverviewTab({ finance, onGoTo }) {
    const { data, failed } = finance;
    if (!data) return <Loading failed={failed} />;
    const { streams, catalog, sales, payout_account } = data;
    const main = sales.totals[0];
    return (
        <div className="space-y-6">
            {!payout_account.configured && (
                <div className="flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center">
                    <MdAccountBalanceWallet size={24} className="shrink-0 text-amber-700" />
                    <p className="flex-1 text-sm text-amber-900"><strong>Sem forma de pagamento.</strong> Indica para onde este artista recebe: IBAN ou Multicaixa Express.</p>
                    <button type="button" onClick={() => onGoTo('bank')} className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600">Configurar pagamento</button>
                </div>
            )}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <KindBox kind="streams" icon={<MdHeadset size={18} />}>
                    <div className="grid grid-cols-2 gap-3">
                        <Stat icon={<MdHeadset size={18} />} label="Reproduções · 30 dias" value={fmtCount(streams.last_30)} hint={<Delta now={streams.last_30} before={streams.prev_30} vs={fmtRange(streams.window?.prev_from, streams.window?.prev_to) || '30 dias anteriores'} />} />
                        <Stat icon={<MdPeople size={18} />} label="Ouvintes · 30 dias" value={fmtCount(streams.listeners_30)} hint={`${fmtCount(streams.total)} reproduções desde sempre`} />
                    </div>
                </KindBox>
                <KindBox kind="sales" icon={<MdPayments size={18} />}
                    action={<button type="button" onClick={() => onGoTo('finance')} className="text-xs text-gray-600 hover:underline">Detalhe ›</button>}>
                    <div className="grid grid-cols-2 gap-3">
                        <Stat icon={<MdPayments size={18} />} label="Total vendido" value={main ? fmtMoney(main.gross, main.currency) : '—'}
                            hint={sales.totals.length > 1 ? `+ ${sales.totals.slice(1).map((t) => fmtMoney(t.gross, t.currency)).join(' + ')}` : main ? `${main.sales} ${main.sales === 1 ? 'compra' : 'compras'}, valor bruto` : 'Ainda nenhuma compra'} />
                        <Stat icon={<MdHourglassEmpty size={18} />} label="Por confirmar" value={fmtCount(sales.pending)} hint={sales.pending ? 'Ainda não contam no total' : 'Nada à espera'} />
                    </div>
                </KindBox>
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
                <section>
                    <h3 className="font-semibold text-gray-900">Últimos lançamentos</h3>
                    <p className="mb-2 text-xs text-gray-500">
                        {catalog.songs} {catalog.songs === 1 ? 'música' : 'músicas'} · {catalog.releases} {catalog.releases === 1 ? 'lançamento' : 'lançamentos'}
                        {catalog.pending > 0 && <span className="text-amber-700"> · {catalog.pending} em revisão</span>}
                    </p>
                    {catalog.latest.length === 0 ? (
                        <p className={`${card} p-6 text-center text-sm text-gray-500`}>Ainda sem lançamentos.</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {catalog.latest.map((r) => (
                                <Link key={r.id} to={`/release/${r.id}`} className="group min-w-0">
                                    <div className="mb-1.5 aspect-square overflow-hidden rounded-lg bg-gray-100">
                                        {r.artwork ? <img src={r.artwork} alt="" className="h-full w-full object-cover" />
                                            : <div className="flex h-full w-full items-center justify-center text-gray-400"><MdAlbum size={28} /></div>}
                                    </div>
                                    <p className="truncate text-sm font-medium text-gray-900 group-hover:underline">{r.title}</p>
                                    <p className="text-xs text-gray-500">{TYPE_LABEL[r.type] || r.type}{r.status === 'draft' && <span className="text-amber-700"> · Rascunho</span>}</p>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
                <section>
                    <div className="mb-2 flex items-baseline justify-between">
                        <h3 className="font-semibold text-gray-900">Mais ouvidas · 30 dias</h3>
                        <button type="button" onClick={() => onGoTo('audience')} className="text-xs text-gray-600 hover:underline">Audiência ›</button>
                    </div>
                    <ul className={`${card} divide-y divide-gray-100 [&>li]:!p-0`}>
                        {streams.top_tracks.length === 0 ? <li><p className="p-4 text-sm text-gray-500">Sem reproduções nos últimos 30 dias.</p></li>
                            : streams.top_tracks.map((t, i) => (
                                <li key={t.music_id}><div className="flex items-center gap-3 px-4 py-2.5 text-sm">
                                    <span className="w-4 tabular-nums text-gray-400">{i + 1}</span>
                                    <span className="min-w-0 flex-1 truncate text-gray-900">{t.title}</span>
                                    <span className="tabular-nums text-gray-600">{fmtCount(t.streams)}</span>
                                </div></li>
                            ))}
                    </ul>
                </section>
            </div>
            <HowToRead />
        </div>
    );
}

export function FinanceTab({ finance, onGoTo }) {
    const { data, failed } = finance;
    if (!data) return <Loading failed={failed} />;
    const { sales, payout_account } = data;
    const totalSales = sales.totals.reduce((n, t) => n + t.sales, 0);
    return (
        <div className="space-y-6">
            <KindBox kind="sales" icon={<MdPayments size={18} />}>
            <p className="-mt-2 mb-4 text-sm text-gray-600">Compras dos álbuns, EP ou singles na loja da Izenu. Valores brutos — antes da parte da Izenu — na moeda em que foram pagos.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Stat icon={<MdPayments size={18} />} label="Vendas totais" value={moneyList(sales.totals)} hint={`${totalSales} ${totalSales === 1 ? 'venda concluída' : 'vendas concluídas'}`} />
                <Stat icon={<MdCalendarToday size={18} />} label="Este mês" value={moneyList(sales.this_month)} hint={sales.this_month.length ? `${sales.this_month.reduce((n, t) => n + t.sales, 0)} vendas` : 'Ainda sem vendas este mês'} />
                <Stat icon={<MdHourglassEmpty size={18} />} label="Por confirmar" value={fmtCount(sales.pending)} hint="Pagamentos ainda não confirmados" />
            </div>
            <SalesTables sales={sales} />
            </KindBox>

            <div className={`${card} flex flex-col gap-3 p-4 sm:flex-row sm:items-center`}>
                <MdAccountBalanceWallet size={22} className="shrink-0 text-amber-600" />
                <p className="flex-1 text-sm text-gray-600">
                    {payout_account.configured ? (payout_account.method === 'multicaixa_express' ? 'Recebe por Multicaixa Express.' : 'Recebe por transferência bancária.') : 'Ainda sem forma de pagamento.'}
                </p>
                <button type="button" onClick={() => onGoTo('bank')} className="text-sm font-medium text-amber-700 hover:underline">
                    {payout_account.configured ? 'Ver dados bancários' : 'Configurar pagamento'}
                </button>
            </div>
            <HowToRead />
        </div>
    );
}

const ANGOLAN_BANKS = [
    'Banco Angolano de Investimentos (BAI)', 'Banco de Fomento Angola (BFA)', 'Banco BIC', 'Banco Millennium Atlântico (BMA)', 'Banco Sol',
    'Banco de Poupança e Crédito (BPC)', 'Banco Económico', 'Banco Caixa Geral Angola', 'Standard Bank Angola', 'Banco Comercial e Industrial (BCI)',
    'Banco Keve', 'Banco Yetu', 'Banco de Negócios Internacional (BNI)', 'Banco de Desenvolvimento de Angola (BDA)', 'Access Bank Angola',
    'Finibanco Angola', 'Banco VTB África', 'Banco Valor', 'Banco Comercial do Huambo (BCH)', 'Banco Kwanza Investimento (BKI)',
];
const groupIban = (v) => v.replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

function Segmented({ value, options, onChange }) {
    return (
        <div role="radiogroup" className="inline-flex flex-wrap overflow-hidden rounded-full !border !border-solid border-gray-300">
            {options.map(([v, l]) => (
                <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)}
                    className={`px-4 py-1.5 text-sm font-medium ${value === v ? 'bg-amber-500 !text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{l}</button>
            ))}
        </div>
    );
}

/** The payout form. With an artistId: that artist's account. With `own`: the
 *  distributor's own account, where the Izenu pays what is owed for its artists. */
export function BankTab({ artistId, own = false }) {
    const endpoint = own ? '/distributor/payouts/account.php' : '/distributor/artist/payoutAccount.php';
    const [form, setForm] = useState(null);
    const [account, setAccount] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        izenuAPI.get(endpoint, { params: own ? {} : { artist_id: artistId } })
            .then(({ data }) => {
                setAccount(data?.data || null);
                // No account yet: the distributor's own page offers what Definições already has.
                const a = data?.data || data?.prefill || null;
                const country = a?.country || (a?.iban?.startsWith('PT') ? 'PT' : 'AO');
                const known = a?.bank_name && ANGOLAN_BANKS.includes(a.bank_name);
                setForm({
                    holderType: a?.holder_type || 'individual', holderName: a?.holder_name || '', taxId: a?.tax_id || '',
                    country, method: a?.method || 'bank_transfer',
                    bank: country === 'AO' ? (known ? a.bank_name : a?.bank_name ? '__other' : '') : '',
                    otherBank: country === 'AO' ? (known ? '' : a?.bank_name || '') : a?.bank_name || '',
                    iban: a?.iban ? groupIban(a.iban) : '', swift: a?.swift_bic || '', phone: a?.mcx_phone || '',
                });
            })
            .catch(() => setError('Não foi possível carregar os dados bancários.'));
    }, [artistId, endpoint, own]);

    if (!form) return <Loading failed={!!error} />;
    const set = (p) => { setForm((f) => ({ ...f, ...p })); setSaved(false); };
    const angola = form.country === 'AO';
    const mcx = angola && form.method === 'multicaixa_express';
    const company = form.holderType === 'company';

    const save = async () => {
        setError(''); setSaving(true);
        try {
            const { data } = await izenuAPI.post(endpoint, {
                ...(own ? {} : { artist_id: artistId }), holder_type: form.holderType, holder_name: form.holderName.trim(), tax_id: form.taxId.trim(),
                country: form.country, method: mcx ? 'multicaixa_express' : 'bank_transfer',
                bank_name: mcx ? '' : (angola && form.bank !== '__other' ? form.bank : form.otherBank.trim()),
                iban: mcx ? '' : form.iban, swift_bic: mcx ? '' : form.swift.trim(), mcx_phone: mcx ? form.phone : '',
            });
            setAccount(data.data); setSaved(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Não foi possível guardar.');
        }
        setSaving(false);
    };

    const label = (t, req) => <span className="mb-1 block text-sm font-medium text-gray-700">{t}{req && <span className="text-red-500"> *</span>}</span>;

    return (
        <div className="max-w-3xl space-y-5">
            {account && !account.complete && (
                <p className="flex gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <MdInfoOutline size={18} className="mt-px shrink-0" />
                    Há dados antigos {own ? 'desta conta' : 'deste artista'} (banco e IBAN). Confirma-os e completa o titular e o NIF.
                </p>
            )}
            {account?.complete && <p className="flex items-center gap-1.5 text-sm text-green-700"><MdCheckCircle size={18} /> Conta configurada · actualizada a {fmtDate(account.updated_at)}</p>}

            <section className={`${card} space-y-4 p-4 sm:p-5`}>
                <h3 className="font-semibold text-gray-900">Titular</h3>
                <Segmented value={form.holderType} onChange={(v) => set({ holderType: v })} options={[['individual', 'Pessoa singular'], ['company', 'Empresa']]} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_200px]">
                    <label className="block">{label(company ? 'Denominação social' : 'Nome completo', true)}
                        <input className={inputCls} value={form.holderName} maxLength={150} onChange={(e) => set({ holderName: e.target.value })}
                            placeholder={company ? 'Como consta no registo comercial' : 'Como consta no documento de identificação'} /></label>
                    <label className="block">{label('NIF', true)}
                        <input className={inputCls} value={form.taxId} maxLength={20} onChange={(e) => set({ taxId: e.target.value })} />
                        {!company && angola && <span className="mt-1 block text-xs text-gray-500">Pessoa singular: o NIF é o número do BI.</span>}</label>
                </div>
            </section>

            <section className={`${card} space-y-4 p-4 sm:p-5`}>
                <h3 className="font-semibold text-gray-900">Forma de recebimento</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block">{label('País da conta')}
                        <select className={inputCls} value={form.country} onChange={(e) => set({ country: e.target.value, method: e.target.value === 'AO' ? form.method : 'bank_transfer' })}>
                            <option value="AO">Angola</option><option value="PT">Portugal</option><option value="EU">Outro país da zona SEPA</option>
                        </select></label>
                    {angola && <div>{label('Recebimento')}<Segmented value={form.method} onChange={(v) => set({ method: v })} options={[['bank_transfer', 'Transferência (IBAN)'], ['multicaixa_express', 'Multicaixa Express']]} /></div>}
                </div>
                {mcx ? (
                    <label className="block max-w-xs">{label('Número Multicaixa Express', true)}
                        <span className="flex items-center gap-2"><span className="text-sm text-gray-500">+244</span>
                            <input className={inputCls} inputMode="tel" maxLength={13} value={form.phone} placeholder="9XX XXX XXX" onChange={(e) => set({ phone: e.target.value.replace(/[^\d\s]/g, '') })} /></span></label>
                ) : (
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {angola ? (
                                <label className="block">{label('Banco', true)}
                                    <select className={inputCls} value={form.bank} onChange={(e) => set({ bank: e.target.value })}>
                                        <option value="" disabled>Escolhe o banco</option>
                                        {ANGOLAN_BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
                                        <option value="__other">Outro banco</option>
                                    </select></label>
                            ) : (
                                <label className="block">{label('Banco', true)}<input className={inputCls} value={form.otherBank} maxLength={100} onChange={(e) => set({ otherBank: e.target.value })} placeholder="Ex.: Caixa Geral de Depósitos" /></label>
                            )}
                            {angola && form.bank === '__other' && (
                                <label className="block">{label('Nome do banco', true)}<input className={inputCls} value={form.otherBank} maxLength={100} onChange={(e) => set({ otherBank: e.target.value })} /></label>
                            )}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_180px]">
                            <label className="block">{label('IBAN', true)}
                                <input className={`${inputCls} font-mono tracking-wide`} value={form.iban} maxLength={42} onChange={(e) => set({ iban: groupIban(e.target.value) })}
                                    placeholder={angola ? 'AO06 0000 0000 0000 0000 0000 0' : form.country === 'PT' ? 'PT50 0000 0000 0000 0000 0000 0' : 'Começa pelo código do país'} />
                                <span className="mt-1 block text-xs text-gray-500">{angola ? '25 caracteres: AO06 seguido dos 21 algarismos da conta.' : 'Confirma no extracto ou na app do banco.'}</span></label>
                            <label className="block">{label('SWIFT / BIC')}
                                <input className={`${inputCls} font-mono tracking-wide`} value={form.swift} maxLength={11} onChange={(e) => set({ swift: e.target.value.toUpperCase() })} placeholder={angola ? 'Opcional' : 'Ex.: CGDIPTPL'} /></label>
                        </div>
                    </div>
                )}
            </section>

            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
                <p className="flex flex-1 items-start gap-1.5 text-xs text-gray-500"><MdLock size={14} className="mt-px shrink-0" /> {own ? 'Só a distribuidora e a equipa financeira da Izenu vêem estes dados.' : 'Só o artista, a distribuidora e a equipa financeira da Izenu vêem estes dados.'}</p>
                {saved && <span className="text-sm text-green-700">Guardado</span>}
                <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold !text-white hover:bg-amber-600 disabled:opacity-60">
                    {saving ? 'A guardar…' : 'Guardar dados bancários'}
                </button>
            </div>
        </div>
    );
}

function SalesTables({ sales }) {
    return (<div className="mt-5 space-y-5">
            <section>
                <h3 className="mb-2 font-semibold text-gray-900">Por lançamento</h3>
                {sales.by_release.length === 0 ? <p className={`${card} p-4 text-sm text-gray-500`}>Ainda nenhum lançamento vendido. Para vender, abre um álbum em Álbuns / EPs e activa a venda.</p> : (
                    <div className={`${card} overflow-x-auto`}>
                        <table className="w-full min-w-[520px] text-sm">
                            <thead><tr className="text-left text-xs uppercase text-gray-500">
                                <th className="px-4 py-2.5 font-semibold">Lançamento</th><th className="px-4 py-2.5 text-right font-semibold">Vendas</th>
                                <th className="px-4 py-2.5 text-right font-semibold">Valor bruto</th><th className="px-4 py-2.5 text-right font-semibold">Última venda</th>
                            </tr></thead>
                            <tbody>{sales.by_release.map((r) => (
                                <tr key={`${r.id || 'album'}-${r.title}`} className="border-t border-gray-100">
                                    <td className="px-4 py-2.5">{r.id ? <Link to={`/release/${r.id}`} className="text-gray-900 hover:underline">{r.title}</Link> : <span className="text-gray-900">{r.title}</span>}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{r.sales}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">{[r.gross_aoa > 0 && fmtMoney(r.gross_aoa, 'AOA'), r.gross_usd > 0 && fmtMoney(r.gross_usd, 'USD'), r.gross_eur > 0 && fmtMoney(r.gross_eur, 'EUR')].filter(Boolean).join(' + ')}</td>
                                    <td className="px-4 py-2.5 text-right text-gray-500">{fmtDate(r.last_sale_at)}</td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                )}
            </section>
            {sales.recent.length > 0 && (
                <section>
                    <h3 className="mb-2 font-semibold text-gray-900">Últimas compras</h3>
                    <div className={`${card} overflow-x-auto`}>
                        <table className="w-full min-w-[560px] text-sm">
                            <thead><tr className="text-left text-xs uppercase text-gray-500">
                                <th className="px-4 py-2.5 font-semibold">Data</th><th className="px-4 py-2.5 font-semibold">Lançamento</th><th className="px-4 py-2.5 font-semibold">Pagamento</th>
                                <th className="px-4 py-2.5 text-right font-semibold">Valor</th><th className="px-4 py-2.5 text-right font-semibold">Estado</th>
                            </tr></thead>
                            <tbody>{sales.recent.map((s, i) => (
                                <tr key={i} className="border-t border-gray-100">
                                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">{fmtDate(s.created_at)}</td>
                                    <td className="px-4 py-2.5 text-gray-900">{s.title}</td>
                                    <td className="px-4 py-2.5 text-gray-600">{METHOD[s.method] || s.method || '—'}</td>
                                    <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-gray-900">{fmtMoney(s.amount, s.currency)}</td>
                                    <td className="px-4 py-2.5 text-right"><span className={`text-xs font-semibold ${s.status === 'completed' ? 'text-green-700' : 'text-amber-700'}`}>{s.status === 'completed' ? 'Concluída' : 'Por confirmar'}</span></td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                </section>
            )}
    </div>);
}

const PAYOUT_METHOD = { bank_transfer: 'Transferência bancária', multicaixa_express: 'Multicaixa Express', other: 'Outro' };

/** "Ganhos": the artist's share of each sale and the royalties of closed months.
 *  When the Izenu pays this distributor (payout_to 'distributor') the money is in
 *  the distributor's own balance (Pagamentos); otherwise the artist asks for it
 *  in Estúdio → Ganhos — the same figures as there. */
export function EarningsTab({ artistId, onGoTo }) {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let alive = true;
        setData(null);
        setFailed(false);
        izenuAPI.get('/distributor/artist/earnings.php', { params: { artist_id: artistId } })
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => { if (alive) setFailed(true); });
        return () => { alive = false; };
    }, [artistId]);

    if (failed) return <p className="text-sm text-red-600">Não foi possível carregar os ganhos.</p>;
    if (!data) return <p className="text-sm text-gray-500">A carregar…</p>;

    const pct = Number(data.share_pct || 70);
    const viaDistributor = !!data.paid_through;
    const balances = data.balances || [];
    // Paid through the distributor: no balance of their own, only what the work earned.
    const earned = {};
    (data.sales || []).forEach((s) => { earned[s.currency] = (earned[s.currency] || 0) + Number(s.artist_amount); });
    const royaltiesTotal = (data.royalties || []).reduce((t, r) => t + Number(r.amount_aoa), 0);

    return (
        <div className="space-y-6">
            <div className={`${card} flex items-start gap-3 p-4 text-sm text-gray-600`}>
                <MdInfoOutline className="mt-0.5 shrink-0 text-amber-600" size={18} />
                <p>
                    O artista fica com <b className="text-gray-900">{pct}% de cada venda</b>, sobre o preço pago pelo fã
                    (vendas desde {fmtDate(data.since)}), e com os <b className="text-gray-900">royalties de streaming</b> dos meses fechados.{' '}
                    {viaDistributor ? (
                        <>A Izenu paga estes valores <b className="text-gray-900">à distribuidora</b>: estão no saldo da página{' '}
                            <Link to="/payouts" className="font-semibold text-amber-700 hover:underline">Pagamentos</Link>, e a distribuidora paga ao artista conforme o vosso contrato.</>
                    ) : (
                        <>O artista pede o levantamento no Estúdio a partir de {fmtMoney(Number(data.min_withdrawal?.AOA || 20000), 'AOA')}, para os{' '}
                            <button type="button" onClick={() => onGoTo('bank')} className="font-semibold text-amber-700 hover:underline">dados bancários</button>.</>
                    )}
                </p>
            </div>

            {viaDistributor ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {Object.entries(earned).map(([c, v]) => (
                        <Stat key={c} icon={<MdPayments />} label={`Vendas (${pct}%)`} value={fmtMoney(v, c)} />
                    ))}
                    <Stat icon={<MdHeadset />} label="Royalties" value={fmtMoney(royaltiesTotal, 'AOA')} hint={`${(data.royalties || []).length} meses fechados`} />
                </div>
            ) : balances.length === 0 ? (
                <div className={`${card} p-4 text-sm text-gray-500`}>Ainda não há ganhos.</div>
            ) : balances.map((b) => (
                <div key={b.currency} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Stat icon={<MdAccountBalanceWallet />} label="Disponível para levantar" value={fmtMoney(b.available, b.currency)}
                        hint={b.pending > 0 ? `${fmtMoney(b.pending, b.currency)} pedidos, em análise` : `Mínimo ${fmtMoney(Number(b.min || 0), b.currency)}`} />
                    <Stat icon={<MdPayments />} label="Ganho" value={fmtMoney(b.earned, b.currency)}
                        hint={[b.sales_earned > 0 && `Vendas ${fmtMoney(b.sales_earned, b.currency)}`, b.royalties_earned > 0 && `Royalties ${fmtMoney(b.royalties_earned, b.currency)}`].filter(Boolean).join(' · ') || undefined} />
                    <Stat icon={<MdCheckCircle />} label="Já recebido" value={fmtMoney(b.paid, b.currency)} />
                </div>
            ))}

            {(data.royalties || []).length > 0 && (
                <section>
                    <h3 className="mb-2 text-base font-semibold text-gray-900">Royalties de streaming</h3>
                    <div className={`${card} overflow-x-auto`}>
                        <table className="w-full min-w-[420px] text-sm">
                            <thead><tr className="text-left text-xs uppercase text-gray-500">
                                <th className="px-4 py-2.5 font-semibold">Mês</th>
                                <th className="px-4 py-2.5 text-right font-semibold">Reproduções pagas</th>
                                <th className="px-4 py-2.5 text-right font-semibold">Royalties</th>
                            </tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.royalties.map((r) => (
                                    <tr key={r.period}>
                                        <td className="px-4 py-2.5 capitalize text-gray-900">{fmtMonth(r.period)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{fmtCount(Number(r.streams))}</td>
                                        <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">{fmtMoney(Number(r.amount_aoa), 'AOA')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {(data.sales || []).length > 0 && (
                <section>
                    <h3 className="mb-2 text-base font-semibold text-gray-900">Vendas</h3>
                    <div className={`${card} overflow-x-auto`}>
                        <table className="w-full min-w-[520px] text-sm">
                            <thead><tr className="text-left text-xs uppercase text-gray-500">
                                <th className="px-4 py-2.5 font-semibold">Data</th>
                                <th className="px-4 py-2.5 font-semibold">Lançamento</th>
                                <th className="px-4 py-2.5 text-right font-semibold">Preço pago</th>
                                <th className="px-4 py-2.5 text-right font-semibold">Parte do artista</th>
                            </tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.sales.map((s) => (
                                    <tr key={s.id}>
                                        <td className="px-4 py-2.5 text-gray-500">{fmtDate(s.created_at)}</td>
                                        <td className="px-4 py-2.5"><Link to={`/release/${s.release_id}`} className="text-gray-900 hover:underline">{s.release_title}</Link></td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{fmtMoney(Number(s.amount), s.currency)}</td>
                                        <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">{fmtMoney(Number(s.artist_amount), s.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <section>
                <h3 className="mb-2 text-base font-semibold text-gray-900">Pagamentos feitos pela Izenu ao artista</h3>
                {(data.payouts || []).length === 0 ? (
                    <div className={`${card} p-4 text-sm text-gray-500`}>Ainda não há pagamentos.</div>
                ) : (
                    <div className={`${card} overflow-x-auto`}>
                        <table className="w-full min-w-[520px] text-sm">
                            <thead><tr className="text-left text-xs uppercase text-gray-500">
                                <th className="px-4 py-2.5 font-semibold">Data</th>
                                <th className="px-4 py-2.5 font-semibold">Tipo</th>
                                <th className="px-4 py-2.5 font-semibold">Como</th>
                                <th className="px-4 py-2.5 font-semibold">Referência</th>
                                <th className="px-4 py-2.5 text-right font-semibold">Valor</th>
                            </tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.payouts.map((p) => (
                                    <tr key={p.id}>
                                        <td className="px-4 py-2.5 text-gray-500">{fmtDate(p.paid_at)}</td>
                                        <td className="px-4 py-2.5 text-gray-900">{p.type === 'incentive' ? `Incentivo da campanha${p.release_title ? ` · ${p.release_title}` : ''}` : 'Levantamento do saldo'}</td>
                                        <td className="px-4 py-2.5 text-gray-600">{PAYOUT_METHOD[p.method] || p.method}</td>
                                        <td className="px-4 py-2.5 text-gray-600">{p.reference || '—'}</td>
                                        <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">{fmtMoney(Number(p.amount), p.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
